import Phaser from 'phaser';
import { Damageable } from '../systems/CombatSystem';

export enum BuildingState {
  INTACT = 0,
  DAMAGED = 1,
  DESTROYED = 2,
}

const BUILDING_MAX_HP = 3;
const STATE_COLORS: Record<BuildingState, number> = {
  [BuildingState.INTACT]: 0x556677,
  [BuildingState.DAMAGED]: 0x8b4513,
  [BuildingState.DESTROYED]: 0x444444,
};

export class DestructibleBuilding extends Phaser.GameObjects.Container implements Damageable {
  private hp: number = BUILDING_MAX_HP;
  private buildingState: BuildingState = BuildingState.INTACT;
  private buildingRect: Phaser.GameObjects.Rectangle;
  readonly isDestroyTarget: boolean;
  private destroyed: boolean = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    widthPx: number,
    heightPx: number,
    isDestroyTarget: boolean = false
  ) {
    super(scene, x, y);
    this.isDestroyTarget = isDestroyTarget;

    this.buildingRect = scene.add.rectangle(0, 0, widthPx, heightPx, STATE_COLORS[BuildingState.INTACT])
      .setStrokeStyle(2, 0x222222);

    // Roof detail line
    const roofLine = scene.add.rectangle(0, -heightPx * 0.2, widthPx * 0.8, 3, 0x334455);

    this.add([this.buildingRect, roofLine]);
    scene.add.existing(this as unknown as Phaser.GameObjects.GameObject);
    this.setDepth(6);

    if (isDestroyTarget) {
      // Red outline to mark it as an objective
      this.buildingRect.setStrokeStyle(3, 0xff2222);
    }
  }

  isDead(): boolean {
    return this.destroyed;
  }

  takeDamage(amount: number): void {
    if (this.destroyed) return;
    this.hp -= amount;

    if (this.hp <= 0) {
      this.hp = 0;
      this.transitionTo(BuildingState.DESTROYED);
    } else if (this.hp === 1) {
      this.transitionTo(BuildingState.DAMAGED);
    }
  }

  private transitionTo(newState: BuildingState): void {
    this.buildingState = newState;
    this.buildingRect.setFillStyle(STATE_COLORS[newState]);

    if (newState === BuildingState.DESTROYED) {
      this.destroyed = true;
      this.setAlpha(0.35);
      // Emit event so GameScene can check objectives
      this.scene.events.emit('buildingDestroyed', this);
    } else if (newState === BuildingState.DAMAGED) {
      // Flash to indicate damage
      this.scene.tweens.add({
        targets: this,
        alpha: 0.4,
        duration: 80,
        yoyo: true,
      });
    }
  }

  getState(): BuildingState {
    return this.buildingState;
  }
}
